import PropTypes from 'prop-types'
import { useRef, useState, useEffect } from 'react'
import { useRouter } from 'next/router'
import Button from 'components/Button'
import useContent from 'store/content'
import { testDispatcherAnswer } from 'libs/localApi'

import {
  createCookieWithTimeParams,
  validateDispatcherCookie,
  redirectDispatcherCookie,
  isLinkInternal,
  getRouterLink,
  setCurrentDomainToLink,
  createCookieWithcontent
} from 'utils'

import {
  StyledSmallLabel,
  StyledForm,
  StyledInput,
  StyledLabel,
  StyledLoader,
  StyledGoInput,
  StyleGoButton,
  StyledGoEmojiContainer,
  StyleEmojiButton,
  StyleButton,
} from './styles'
import { useOnlineStatus } from '../../hooks/useOnlineStatus'
import PwaOfflinePlaceholder from 'components/PwaOfflinePlaceholder'
import { withUtm } from 'utils/utm'

const KNOWN_VALUE = ' * * * *'

const Dispatcher = ({ part, themeInfos }) => {
  const online = useOnlineStatus()

  const [valid, setValid] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [loading, setLoading] = useState(false)
  const [typeButton, setTypeButton] = useState('')
  const [redirectCookie, setRedirectCookie] = useState('')

  const content = useContent()
  const dispatcherInfos = content.get.dispatcherById(part.id)

  const inputEl = useRef(null)
  const router = useRouter()
  const appId = content.get.appId()

  useEffect(() => {
    if (dispatcherInfos) {
      setTypeButton(detectStringCase(dispatcherInfos.display.textButton))
    }
  }, [dispatcherInfos]);

  useEffect(() => {
    const hasCookie = validateDispatcherCookie(dispatcherInfos)
    setValid(hasCookie)
    setSubmitted(hasCookie)

    if(hasCookie) {
      const redirectCookie = redirectDispatcherCookie(dispatcherInfos)
      if(redirectCookie) {
        setRedirectCookie(redirectCookie)
      }
    }
  }, [])

  if (!online) {
    return <PwaOfflinePlaceholder />
  }

  if (!dispatcherInfos) {
    return null
  }

  const detectStringCase = (input) => {
    const goRegex = /^<p>(Go !|Go!|go !|go!|GO !|GO!|)<\/p>$/

    if (goRegex.test(input)) {
      return 'go'
    }

    return isEmoji(input) ? 'emoji': 'text';
  }

  const isEmoji = (htmlString) => {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = htmlString;

    const pElement = tempDiv.querySelector('p');

    if (!pElement) {
      return false;
    }

    const spans = pElement.querySelectorAll('span.ql-emojiblot');

    if (spans.length === 1) {
      const span = spans[0];
      const emoji = span.querySelector('span.ap');

      if (emoji) {
        const textContent = pElement.textContent.trim();
        if (textContent === emoji.textContent.trim()) {
          return true;
        }
      }
    }

    return false;
  }

 const getIsInvalid = () => {
    const { onError } = dispatcherInfos

    if (onError) {
      const isInvalid = submitted && !valid
      return isInvalid && onError
    }

    return false
  }

  const redirect = (url) => {
   setLoading(true)
   setTimeout(() => {
     if (isLinkInternal(url)) {
       const { href, hardNavigation } = getRouterLink(url, router.query.appId, router.asPath)
       if (hardNavigation) {
         window.location.href = withUtm(href)
       } else {
         router.push(withUtm(href))
       }
     } else {
       window.location.href = withUtm(setCurrentDomainToLink(url))
     }
     setLoading(false)
   }, 750)
 }

  const handleOnSubmit = async (e) => {
    const { id } = dispatcherInfos

    e.preventDefault()

    const pass = inputEl.current.value

    const response = pass !== '' ? await testDispatcherAnswer(appId, id, pass) : valid && submitted

    setSubmitted(true)

    if (response.valid) {

      setValid(response.valid)
      createCookieWithcontent(`dispatcher-redirect-${id}`, response.data.redirect);
      createCookieWithcontent(`dispatcher-rep${response.data.idResponse}-${id}`, response.data.redirect);
      createCookieWithcontent(`dispatcher-redirect-${id}`, response.data.redirect);

      if(response.expiration) {
        createCookieWithTimeParams(`dispatcher-${id}`, response.data.unlockTime.value, response.data.unlockTime.unit)
      } else {
        createCookieWithTimeParams(`dispatcher-${id}`)
      }

      redirect(response.data.redirect)
      return;
    }

    if(!response.valid && response && !response.data) {
      setValid(true)
      redirect(redirectCookie)
    }

    return false
  }

  const {
    onError,
    display: { title, textBefore, textButton, textAfter, color, subtitle, frame },
  } = dispatcherInfos

  return (
    <>
      <StyledForm autoComplete="off" onSubmit={handleOnSubmit} $color={color} $border={frame} $themeCustom={themeInfos}>
        {title && title.length > 0 && <h2>{title}</h2>}
        {subtitle && subtitle.length > 0 && <h3>{subtitle}</h3>}

        {textBefore && (
          <StyledLabel htmlFor={`dispatcherpass-${part.id}`} style={{ marginBottom: '20px' }}>
            <div dangerouslySetInnerHTML={{ __html: textBefore }} />
          </StyledLabel>
        )}

        {typeButton === 'go' && (
          <>
            <StyledGoEmojiContainer>
              <StyledGoInput
                ref={inputEl}
                name={`dispatcherpass-${part.id}`}
                id={`dispatcherpass-${part.id}`}
                placeholder={submitted && valid ? KNOWN_VALUE : ''}
                $error={submitted && !valid}
                $valid={submitted && valid}
              />
              <StyleGoButton>
                <Button id="dispatcher submit button" onClick={handleOnSubmit} disabled={loading} isLock themeInfos={themeInfos}>
                  <>
                    {loading && <StyledLoader />}
                    <span dangerouslySetInnerHTML={{ __html: textButton }} />
                  </>
                </Button>
              </StyleGoButton>
            </StyledGoEmojiContainer>
            {getIsInvalid() && <StyledSmallLabel $state="error">{onError}</StyledSmallLabel>}
          </>
        )}

        {typeButton === 'emoji' && (
          <>
            <StyledGoEmojiContainer>
              <StyledGoInput
                ref={inputEl}
                name={`dispatcherpass-${part.id}`}
                id={`dispatcherpass-${part.id}`}
                placeholder={submitted && valid ? KNOWN_VALUE : ''}
                $error={submitted && !valid}
                $valid={submitted && valid}
              />
              <StyleEmojiButton>
                <Button id="dispatcher submit button" onClick={handleOnSubmit} disabled={loading} isLock themeInfos={themeInfos}>
                  <>
                    {loading && <StyledLoader />}
                    <span dangerouslySetInnerHTML={{ __html: textButton }} />
                  </>
                </Button>
              </StyleEmojiButton>
            </StyledGoEmojiContainer>
            {getIsInvalid() && <StyledSmallLabel state="error">{onError}</StyledSmallLabel>}
          </>
        )}
        {typeButton === 'text' && (
          <>
            <StyledInput
              ref={inputEl}
              name={`dispatcherpass-${part.id}`}
              id={`dispatcherpass-${part.id}`}
              placeholder={submitted && valid ? KNOWN_VALUE : ''}
              $error={submitted && !valid}
              $valid={submitted && valid}
            />
            {getIsInvalid() && <StyledSmallLabel state="error">{onError}</StyledSmallLabel>}
            <StyleButton>
              <Button id="lock submit button" onClick={handleOnSubmit} disabled={loading} isLock themeInfos={themeInfos}>
                <>
                  {loading && <StyledLoader />}
                  <span dangerouslySetInnerHTML={{ __html: textButton }} />
                </>
              </Button>
            </StyleButton>
          </>
        )}

        {textAfter && (
          <StyledLabel htmlFor={`dispatcherpass-${part.id}`} style={{ marginBottom: '20px', marginTop: '10px', textAlign: 'left' }}>
            <div dangerouslySetInnerHTML={{ __html: textAfter }} />
          </StyledLabel>
        )}
      </StyledForm>
    </>
  )
}

Dispatcher.propTypes = {
  part: PropTypes.object,
  themeInfos: PropTypes.object,
  t: PropTypes.func,
}

export default Dispatcher
